/**
 * "What was on screen at this moment" — the panel that reads the recording for
 * the reviewer instead of making them do it.
 *
 * Watching a recording, the question that matters is never "how did this
 * attempt score" but "what was the student looking at *right now*". The API
 * already answers that without a new endpoint: every answer carries
 * `visit_offsets_ms`, the list of instants that question was opened, measured
 * from `attempts.started_at` — the same t=0 the recordings use. Merge those
 * instants across all questions into one chronological list and each visit
 * simply runs until the next visit of any question begins; the last runs to the
 * end of the attempt. That derives a complete "at time T the student was on
 * question N" mapping from data the screen already has.
 *
 * **Render discipline.** This is the only section of the review screen besides
 * the player and the timeline that follows the clock, so it re-renders roughly
 * twelve times a second. Everything expensive is therefore pre-computed once
 * into the span list — the stem excerpt, both answer strings, the correctness
 * verdict — and a tick costs one binary search plus some text. Nothing here
 * renders markdown or math, and nothing here changes the selected question,
 * which is what keeps the question table and the rendered paper out of the
 * per-tick work. See the header comment on `AttemptReviewPage`.
 */
import { memo } from "react";
import { Check, Minus, Pin, PinOff, X } from "lucide-react";
import { Badge, Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { QuestionView, describeAnswer, describeResponse } from "./QuestionView";
import { formatClock, formatDuration, formatMarks } from "../lib/format";
import type { AnswerRow } from "../data/types";
import { plainSummary } from "../QuestionsPage";

/**
 * How a question was graded, in the three states a reviewer actually acts on —
 * plus `ungraded`, which is what a submitted answer looks like before the
 * scorer has had an opinion about it (a NAT awaiting a manual override).
 */
export type AnswerState = "correct" | "wrong" | "unanswered" | "ungraded";

/** One continuous stretch of the recording during which one question was open. */
export interface QuestionSpan {
  questionId: string;
  /** Position on the paper, 1-based — what the reviewer sees everywhere else. */
  number: number;
  /** Which opening of this question this is, and how many there were. */
  visit: number;
  totalVisits: number;
  startMs: number;
  endMs: number;
  stem: string;
  response: string;
  correct: string;
  state: AnswerState;
  marksAwarded: string;
  marks: string;
}

export function answerStateOf(answer: AnswerRow): AnswerState {
  if (answer.response === null || describeResponse(answer.response) === "—") {
    return "unanswered";
  }
  if (answer.is_correct === true) return "correct";
  if (answer.is_correct === false) return "wrong";
  return "ungraded";
}

/**
 * The chronological question spans for an attempt.
 *
 * A visit ends when the *next* visit of any question begins, because that is
 * the only signal the client sends: the student navigating away is recorded as
 * them arriving somewhere else. Zero-length spans — two visits logged at the
 * same millisecond, which happens when a question is opened and immediately
 * skipped — are dropped, since no playhead position can ever fall inside one.
 */
export function buildQuestionSpans(
  answers: AnswerRow[],
  durationMs: number,
): QuestionSpan[] {
  const visits = answers.flatMap((answer) => {
    const state = answerStateOf(answer);
    const stem = plainSummary(answer.question.body_md);
    const response = describeResponse(answer.response);
    const correct = describeAnswer(answer.question.correct_answer);
    const marksAwarded = formatMarks(answer.marks_awarded);
    const marks = formatMarks(answer.marks);
    return answer.visit_offsets_ms.map((offset, i) => ({
      questionId: answer.question.id,
      number: answer.order_idx + 1,
      visit: i + 1,
      totalVisits: answer.visit_offsets_ms.length,
      startMs: offset,
      endMs: durationMs,
      stem,
      response,
      correct,
      state,
      marksAwarded,
      marks,
    }));
  });

  visits.sort((a, b) => a.startMs - b.startMs);
  for (let i = 0; i < visits.length - 1; i += 1) {
    visits[i]!.endMs = visits[i + 1]!.startMs;
  }
  return visits.filter((visit) => visit.endMs > visit.startMs);
}

/**
 * The span containing `timeMs`, or -1 when the playhead sits before the first
 * question was ever opened — which is where it starts, since the setup footage
 * runs at negative offsets.
 */
export function spanIndexAt(spans: QuestionSpan[], timeMs: number): number {
  if (spans.length === 0 || timeMs < spans[0]!.startMs) return -1;
  let lo = 0;
  let hi = spans.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (spans[mid]!.startMs <= timeMs) lo = mid;
    else hi = mid - 1;
  }
  return timeMs < spans[lo]!.endMs ? lo : -1;
}

const STATE_TEXT: Record<AnswerState, string> = {
  correct: "text-answered",
  wrong: "text-violation",
  unanswered: "text-ink-faint",
  ungraded: "text-review",
};

const STATE_LABEL: Record<AnswerState, string> = {
  correct: "Correct",
  wrong: "Wrong",
  unanswered: "Not answered",
  ungraded: "Not graded",
};

function StateBadge({ state }: { state: AnswerState }) {
  const Icon = state === "correct" ? Check : state === "wrong" ? X : Minus;
  return (
    <Badge
      tone={
        state === "correct"
          ? "answered"
          : state === "wrong"
            ? "violation"
            : state === "ungraded"
              ? "review"
              : "neutral"
      }
    >
      <Icon className="size-3" />
      {STATE_LABEL[state]}
    </Badge>
  );
}

/**
 * The follower panel itself.
 *
 * `pinnedIndex` doubles as the follow switch: `null` means the panel tracks the
 * playhead, a number means the reviewer has parked it on one visit so they can
 * read it while the recording keeps running. Clicking a row in the question
 * table pins too — otherwise the next tick would drag the panel straight back
 * to wherever the video is, and the click would look broken.
 */
export const QuestionFollower = memo(function QuestionFollower({
  spans,
  timeMs,
  pinnedIndex,
  onPin,
  onSeek,
  answers,
}: {
  spans: QuestionSpan[];
  timeMs: number;
  pinnedIndex: number | null;
  onPin: (index: number | null) => void;
  onSeek: (ms: number) => void;
  /**
   * Supplied in review mode only. With it the panel shows the question as the
   * STUDENT saw it — every option, the one they picked, the one that was
   * right — plus how long they spent on it and how many times they came back.
   * Without it the panel stays a one-line summary, because the normal page
   * already has a full panel under the table and two of them is noise.
   */
  answers?: AnswerRow[];
}) {
  const liveIndex = spanIndexAt(spans, timeMs);
  const following = pinnedIndex === null;
  const index = following ? liveIndex : pinnedIndex;
  const span = spans[index] ?? null;

  const detail =
    answers && span
      ? (answers.find((a) => a.question.id === span.questionId) ?? null)
      : null;

  // No Card of its own: it lives inside the player card, in the narrow column
  // beside the screen recording, so the question on screen is visible without
  // scrolling away from the footage it belongs to.
  //
  // Everything below wraps rather than sitting on one row. This used to be a
  // full-width strip; in a fifth of the width its header collapsed into
  // overlapping text with the button on top of the title.
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5 border-b border-border px-3 py-2">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <h2 className="text-sm font-semibold text-ink">
              {span ? `Question ${span.number}` : "On screen"}
            </h2>
            {span ? <StateBadge state={span.state} /> : null}
          </div>
          {span ? (
            <span className="tabular text-2xs text-ink-faint">
              {formatClock(span.startMs)} – {formatClock(span.endMs)} ·{" "}
              {formatDuration(span.endMs - span.startMs)}
              {span.totalVisits > 1
                ? ` · visit ${span.visit} of ${span.totalVisits}`
                : null}
            </span>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {!following && span ? (
            <Button size="sm" variant="ghost" onClick={() => onSeek(span.startMs)}>
              Play from here
            </Button>
          ) : null}
          <Button
            size="sm"
            variant={following ? "primary" : "secondary"}
            icon={following ? <PinOff /> : <Pin />}
            aria-pressed={following}
            onClick={() => onPin(following ? (liveIndex === -1 ? null : liveIndex) : null)}
          >
            {following ? "Following" : "Pinned"}
          </Button>
        </div>
      </div>

      {span && detail ? (
        <div className="flex flex-col gap-2.5 p-3">
          {/* How they worked, before what they answered: on a review screen the
              behaviour is usually the thing you came to see. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-surface-sunken px-2.5 py-1.5 text-2xs">
            <span className="text-ink-muted">
              Time{" "}
              <span className="tabular font-medium text-ink">
                {formatDuration(detail.time_spent_ms)}
              </span>
            </span>
            <span className="text-ink-muted">
              Opened{" "}
              <span className="tabular font-medium text-ink">
                {detail.visit_offsets_ms.length}
              </span>
              {detail.visit_offsets_ms.length === 1 ? " time" : " times"}
            </span>
            <span className="text-ink-muted">
              Marks{" "}
              <span className={cn("tabular font-medium", STATE_TEXT[span.state])}>
                {span.marksAwarded}
              </span>
              <span className="text-ink-faint"> / {span.marks}</span>
            </span>
          </div>

          {/* The paper itself, with the student's choice and the key marked. */}
          <QuestionView
            question={detail.question}
            showAnswer
            answer={{
              response: detail.response,
              is_correct: detail.is_correct,
            }}
          />
        </div>
      ) : span ? (
        <div className="flex flex-col gap-2 p-3">
          <p className="line-clamp-3 min-w-0 text-xs text-ink">{span.stem}</p>

          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-2xs">
            <span className="text-ink-muted">
              Answer{" "}
              <span className={cn("tabular font-medium", STATE_TEXT[span.state])}>
                {span.response}
              </span>
            </span>
            <span className="text-ink-muted">
              Key <span className="tabular font-medium text-ink">{span.correct}</span>
            </span>
            <span className="text-ink-muted">
              Marks{" "}
              <span className="tabular font-medium text-ink">
                {span.marksAwarded}
              </span>
              <span className="text-ink-faint"> / {span.marks}</span>
            </span>
          </div>
        </div>
      ) : (
        <p className="px-4 py-3 text-xs text-ink-faint">
          {spans.length === 0
            ? "No question visit times were recorded for this attempt, so the paper cannot be matched to the recording."
            : "The student had not opened a question yet at this point in the recording."}
        </p>
      )}
    </div>
  );
});
